// The Users page's dialogs (D51, D52): add a user with a role and either a temporary
// password or an invitation link; reset a password (which ends that user's sessions); show
// an invitation link or a one-time device code once. Modal per the mocks' roles-permissions pattern
// (design/isomorphic/src/pages/users.tsx).
import { X } from "@phosphor-icons/react";
import { type FormEvent, type ReactNode, useState } from "react";
import { ActionIcon, Button, Input, Modal, Password } from "rizzui";

import type { User } from "../api/client";
import { addUser, type Invitation, inviteUser, type Role, setUserPassword } from "../auth/admin";
import type { DeviceCode } from "../device/codes";
import { MIN_PASSWORD_CHARS, passwordProblem } from "../auth/api";
import { formatDateTime, useT } from "../i18n";
import { toast } from "../layout/toast";
import { ErrorState } from "../ui/states";

const selectClass =
  "w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none";

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function Dialog({ title, open, onClose, onSubmit, error, submit, children }: {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit?: () => Promise<void>;
  error?: string;
  submit?: string;
  children: ReactNode;
}) {
  const { common, users } = useT();
  const [busy, setBusy] = useState(false);
  async function handle(e: FormEvent) {
    e.preventDefault();
    if (!onSubmit) return onClose();
    setBusy(true);
    try {
      await onSubmit();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal isOpen={open} onClose={onClose} containerClassName="dark:bg-gray-100">
      <form className="p-6" aria-label={title} onSubmit={(e) => void handle(e)}>
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg">{title}</h2>
          <ActionIcon size="sm" variant="text" onClick={onClose} aria-label={common.actions.close}>
            <X weight="bold" className="h-4 w-4" />
          </ActionIcon>
        </div>
        <div className="space-y-4">{children}</div>
        {error ? <ErrorState className="mt-4" title={users.dialogs.notSaved} message={error} /> : null}
        <div className="mt-6 flex justify-end gap-3">
          {onSubmit ? (
            <Button variant="outline" onClick={onClose}>
              {common.actions.cancel}
            </Button>
          ) : null}
          <Button type="submit" isLoading={busy}>
            {submit ?? common.actions.done}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type Access = "invite" | "password";

export function AddUserDialog({ open, roles, onClose, onAdded }: {
  open: boolean;
  roles: Role[];
  onClose: () => void;
  onAdded: (user: User, invitation?: Invitation) => Promise<void>;
}) {
  const t = useT().users.dialogs;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [access, setAccess] = useState<Access>("invite");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();

  async function onSubmit() {
    const problem = access === "password" ? passwordProblem(password) : undefined;
    if (problem) return setError(problem);
    setError(undefined);
    try {
      const user = await addUser({
        email: email.trim(),
        display_name: name.trim(),
        role_id: roleId || undefined,
        password: access === "password" ? password : undefined,
      });
      const link = access === "invite" ? await inviteUser(user.id) : undefined;
      setEmail("");
      setName("");
      setPassword("");
      toast(t.added(user.email));
      onClose();
      await onAdded(user, link);
    } catch (err) {
      setError(message(err));
    }
  }

  return (
    <Dialog title={t.addTitle} open={open} onClose={onClose} onSubmit={onSubmit} error={error} submit={t.addSubmit}>
      <Input type="email" label={t.email} placeholder="ama@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <Input label={t.displayName} placeholder="Ama Mensah" value={name} onChange={(e) => setName(e.target.value)} required />
      {roles.length > 0 ? (
        <label className="block text-sm font-medium">
          {t.role}
          <select className={`${selectClass} mt-1.5`} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">{t.defaultRole}</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <fieldset className="space-y-1.5 text-sm">
        <legend className="mb-1 font-medium">{t.access}</legend>
        <label className="flex items-center gap-2">
          <input type="radio" name="access" checked={access === "invite"} onChange={() => setAccess("invite")} />
          {t.byInvitation}
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" name="access" checked={access === "password"} onChange={() => setAccess("password")} />
          {t.byPassword}
        </label>
      </fieldset>
      {access === "password" ? (
        <Password
          label={t.temporaryPassword}
          autoComplete="new-password"
          placeholder={t.atLeast(MIN_PASSWORD_CHARS)}
          helperText={t.temporaryHelp}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      ) : null}
    </Dialog>
  );
}

export function ResetPasswordDialog({ user, onClose }: { user: User | undefined; onClose: () => void }) {
  const t = useT().users.dialogs;
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();

  async function onSubmit() {
    if (!user) return;
    const problem = passwordProblem(password);
    if (problem) return setError(problem);
    try {
      await setUserPassword(user.id, password);
      toast(t.resetDone(user.email));
      setPassword("");
      onClose();
    } catch (err) {
      setError(message(err));
    }
  }

  return (
    <Dialog
      title={t.resetTitle(user?.email ?? "")}
      open={user !== undefined}
      onClose={onClose}
      onSubmit={onSubmit}
      error={error}
      submit={t.setPassword}
    >
      <p className="m-0 text-[13px] text-gray-500">{t.resetLead}</p>
      <Password label={t.newPassword} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
    </Dialog>
  );
}

export function DeviceCodeDialog({ issued, onClose }: { issued: { user: User; code: DeviceCode } | undefined; onClose: () => void }) {
  const t = useT().users.dialogs;
  return (
    <Dialog title={t.codeTitle(issued?.user.email ?? "")} open={issued !== undefined} onClose={onClose}>
      <p className="m-0 text-[13px] text-gray-500">{t.codeBody(issued ? formatDateTime(issued.code.expires_at) : "")}</p>
      <Input label={t.oneTimeCode} readOnly value={issued?.code.code ?? ""} onFocus={(e) => e.target.select()} inputClassName="font-mono" />
    </Dialog>
  );
}

export function InvitationDialog({ invitation, onClose }: { invitation: { user: User; link: Invitation } | undefined; onClose: () => void }) {
  const t = useT().users.dialogs;
  const url = invitation ? new URL(invitation.link.url, window.location.origin).href : "";
  return (
    <Dialog title={t.invitationTitle(invitation?.user.email ?? "")} open={invitation !== undefined} onClose={onClose}>
      <p className="m-0 text-[13px] text-gray-500">{t.invitationBody(invitation ? formatDateTime(invitation.link.expires_at) : "")}</p>
      <Input label={t.invitationLink} readOnly value={url} onFocus={(e) => e.target.select()} />
      <Button
        variant="outline"
        onClick={() => {
          void navigator.clipboard?.writeText(url).then(() => toast(t.linkCopied));
        }}
      >
        {t.copyLink}
      </Button>
    </Dialog>
  );
}
